import { useState, useEffect } from "react";
import { Box, colors, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Button, CircularProgress } from '@mui/material';

import Auth, { API_URL, generateCSRFToken } from "../../utils/auth";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from 'react-toastify';
import CircleModal from "./Integrations/CircleIntegrations/CircleModal";
import CircleManagement from "./Integrations/CircleIntegrations/CircleManagement";
import SlackModal from "./Integrations/SlackIntegrations/SlackModal";
import HighLevelModal from "./Integrations/HighLevelIntegrations/HighLevelModal";
import ZoomManagement from "./Integrations/ZoomIntegrations/ZoomManagement";
import HighLevelContactSync from "./Integrations/HighLevelIntegrations/HighLevelContactSync";
import "./IntegrationsMain.css";

const IntegrationsMain = ({ integrationStatus }) => {
  const [connectionStatus, setConnectionStatus] = useState(integrationStatus || {});
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [slackModalOpen, setSlackModalOpen] = useState(false);
  const [circleModalOpen, setCircleModalOpen] = useState(false);
  const [circleManagementOpen, setCircleManagementOpen] = useState(false);
  const [highLevelModalOpen, setHighLevelModalOpen] = useState(false);
  const [zoomManagementOpen, setZoomManagementOpen] = useState(false);
  const [highLevelContactSyncModealOpen, setHighLevelContactSyncModalOpen] = useState(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState(null);

  const fetchIntegrationStatuses = async () => {
    setStatusLoading(true);
    setError(null);

    const workspaceId = Auth.getWorkspaceId();
    const user = Auth.getUser();
    const adminUserId = user?.id;

    try {
      const newStatus = { slack: false, "circle-so": false, highlevel: false, zoom: false };

      try {
        const slackResponse = await axios.get(`${API_URL}/slack/status`, {
          headers: {
            Authorization: `Bearer ${Auth.getToken()}`,
          },
        });
        Object.assign(newStatus, slackResponse.data);
      } catch (err) {
        console.error("Failed to fetch Slack integration status:", err);
        newStatus.slack = false;
      }

      if (workspaceId && adminUserId) {
        try {
          const circleResponse = await axios.get(
            `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&adminUserId=${adminUserId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          newStatus["circle-so"] = circleResponse.data.hasBotToken || false;
        } catch (err) {
          console.error("Failed to fetch Circle.so integration status:", err);
          newStatus["circle-so"] = false;
        }
      }

      try {
        // const API_URL = "http://localhost:4000"; // Replace with your actual API URL
        const ghlCBResponse = await axios.get(`${API_URL}/ghlcb/status`, {
          headers: {
            Authorization: `Bearer ${Auth.getToken()}`,
          },
        });

        newStatus.highlevel = ghlCBResponse.data.connected || false;
      } catch (err) {
        console.error("Failed to fetch HighLevel integration status:", err.message);
        newStatus.highlevel = false;
      }

      // Check Zoom integration status
      if (workspaceId && adminUserId) {
        try {
          const zoomResponse = await axios.get(
            `${API_URL}/zoom/zoom-has-integration?workspaceId=${workspaceId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          newStatus.zoom = zoomResponse.data.hasIntegration || false;
        } catch (err) {
          console.error("Failed to fetch Zoom integration status:", err);
          newStatus.zoom = false;
        }
      }

      setConnectionStatus(newStatus);

    } catch (err) {
      console.error("Error fetching integration statuses:", err);
      setError("Failed to fetch integration statuses");
      setConnectionStatus({ slack: false, "circle-so": false, zoom: false });
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrationStatuses();
  }, []);

  // useEffect(() => {
  //   document.body.style.overflow = slackDialogOpen ? "hidden" : "auto";
  // }, [slackDialogOpen]);

  const connectableIntegrations = [
    {
      id: "slack",
      title: "Connect your Slack Channel",
      description:
        'Get instant notifications on your Slack channel. Click "Connect" to add a Slack channel.',
      logo: "/slack.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
    {
      id: "circle-so",
      title: "Connect your Circle Community",
      description:
        "Sync real-time activity scores to your Client Retention Score reports and client dashboards.",
      logo: "/circle_so.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
    {
      id: "highlevel",
      title: "Connect your HighLevel Account",
      description:
        "Sync your HighLevel CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/high_level.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
      refreshText: "Refresh",
    },
    {
      id: "zoom",
      title: "Connect your Zoom Account",
      description:
        "Sync your Zoom account to seamlessly send call transcriptions to ClientBloom.",
      logo: "/Zoom_Logo_Bloom_RGB.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
  ];

  const upcomingIntegrations = [
    {
      id: "gmail",
      title: "Connect your Gmail Account",
      description:
        "Use your Google email provider to send NPS and CES surveys to clients.",
      logo: "/gmail.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "outlook",
      title: "Connect your Outlook Account",
      description:
        "Use your Outlook email provider to send NPS and CES surveys to clients.",
      logo: "/outlook.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "hubspot",
      title: "Connect your HubSpot Account",
      description:
        "Sync your HubSpot CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/hubspot.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "active-campaign",
      title: "Connect your ActiveCampaign Account",
      description:
        "Sync your ActiveCampaign CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/active_campaign.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "salesforce",
      title: "Connect your Salesforce Account",
      description:
        "Sync your Salesforce CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/salesforce.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "google-meet",
      title: "Connect your Google Meet Account",
      description:
        "Sync your Google Meet account to seamlessly send call transcriptions to ClientBloom.",
      logo: "/google_meet.png",
      connectText: "Connect",
      manageText: "Manage",
    },
  ];

  const handleConnect = (id) => {
    setLoadingId(id);
    switch (id) {
      case "slack": {
        const workspaceId = Cookies.get("workspaceId");
        if (!workspaceId) {
          setError("Workspace ID not found in cookies");
          return;
        }
        const state = JSON.stringify({ workspaceId, csrf: generateCSRFToken() });
        window.location.href = `${API_URL}/slack/redirect?state=${encodeURIComponent(state)}`;
        break;
      }
      case "circle-so": {
        setCircleModalOpen(true);
        break;
      }
      case "highlevel":
        //const API_URL = "http://localhost:4000"; // Replace with your actual API URL
        const ghlWorkspaceId = Cookies.get("workspaceId");
        const connectState = JSON.stringify({ workspaceId: ghlWorkspaceId });
        const connectUrl = `${API_URL}/ghlcb/connect?state=${encodeURIComponent(connectState)}`;


        window.location.href = connectUrl;
        break;
      case "zoom": {
        // Zoom connect: Direct navigation to the backend so the browser follows the 302
        const workspaceId = Auth.getWorkspaceId();
        const user = Auth.getUser();
        const userId = user?.id;
        if (!workspaceId || !userId) {
          setError("Workspace ID or User ID not found");
          return;
        }
  // Direct navigation is required so the browser can follow the OAuth 302 redirect for Zoom authentication.
  window.location.href = `${API_URL}/zoom/zoom-connect?workspaceId=${workspaceId}&userId=${userId}`;
        break;
      }
      default:
        console.log(`Connect clicked for ${id}`);
        break;
    }
  };

  const handleDisconnect = async (id) => {
    // For Zoom, show confirmation dialog first
    if (id === "zoom") {
      setIntegrationToDisconnect(id);
      setConfirmDisconnectOpen(true);
      return;
    }

    // For other integrations, proceed directly
    await performDisconnect(id);
  };

  const performDisconnect = async (id) => {
    setDisconnectLoading(true);
    setLoadingId(id);
    try {
      switch (id) {
        case "slack":
          await axios
            .post(
              `${API_URL}/slack/disconnect`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then(() => {
              setConnectionStatus((prev) => ({ ...prev, slack: false }));
              toast.success('Slack integration disconnected successfully!');
            })
            .catch((err) => toast.error(`Failed to disconnect Slack. Error: ${err}`))
            .finally(() => {
              setDisconnectLoading(false);
            });
          break;
        case "circle-so":
          const workspaceId = Auth.getWorkspaceId();
          const user = Auth.getUser();
          const adminUserId = user?.id;
          if (!workspaceId || !adminUserId) {
            setError("Workspace ID or Admin User ID not found");
            return;
          }
          await axios.delete(
            `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&adminUserId=${adminUserId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          await fetchIntegrationStatuses();
          break;
        case "highlevel":
          //const API_URL = "http://localhost:4000"; // Replace with your actual API URL

          await axios
            .post(
              `${API_URL}/ghlcb/disconnect`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then((response) => {
              setConnectionStatus((prev) => ({ ...prev, highlevel: false }));
              toast.success('HighLevel integration disconnected successfully!');
            })
            .catch((err) => {
              toast.error(`Failed to disconnect HighLevel. Error: ${err}`);
            })
            .finally(() => {
              setDisconnectLoading(false);
            });
          break;
        case "zoom":
          const zoomWorkspaceId = Auth.getWorkspaceId();
          const zoomUser = Auth.getUser();
          const zoomUserId = zoomUser?.id;
          if (!zoomWorkspaceId || !zoomUserId) {
            setError("Workspace ID or User ID not found");
            return;
          }
          await axios.delete(
            `${API_URL}/zoom/zoom-disconnect?workspaceId=${zoomWorkspaceId}&userId=${zoomUserId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          await fetchIntegrationStatuses();
          toast.success('Zoom integration disconnected successfully!');
          break;
        default:
            console.log(`Disconnect clicked for ${id}`);
          break;
      }
    } catch (err) {
      setError(`Failed to disconnect ${id}`);
      console.error(err);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    setConfirmDisconnectOpen(false);
    if (integrationToDisconnect) {
      await performDisconnect(integrationToDisconnect);
      setIntegrationToDisconnect(null);
    }
  };

  const handleCancelDisconnect = () => {
    setConfirmDisconnectOpen(false);
    setIntegrationToDisconnect(null);
  };

  const handleRefresh = async (id) => {
    setManageLoading(true);
    setLoadingId(id);
    try {
      switch (id) {
        case "highlevel":
          // const API_URL = "http://localhost:4000"; // Replace with your actual API URL

          await axios
            .get(
              `${API_URL}/ghlcb/refresh`,
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then((response) => {
              toast.success('HighLevel integration refreshed successfully!');
              setConnectionStatus((prev) => ({ ...prev, highlevel: true }));
            })
            .catch((err) => {
              toast.error(`Failed to refresh HighLevel. Error: ${err}`);
              setConnectionStatus((prev) => ({ ...prev, highlevel: "expired" }));
            })
            .finally(() => {
              setManageLoading(false);
            });
          break;
        default:
            console.log(`Refresh clicked for ${id}`);
          break;
      }
    } catch (err) {
      setError(`Failed to refresh ${id}`);
      console.error(err);
    } finally {
      setManageLoading(false);
    }
  };

  const handleManage = (id) => {
    setManageLoading(true);
    setLoadingId(id);
    switch (id) {
      case "slack":
        if (!connectionStatus.slack) {
          setError("Slack is not connected");
          return;
        }
        setSlackModalOpen(true);
        setManageLoading(false);
        break;
      case "circle-so":
        if (!connectionStatus["circle-so"]) {
          setError("Circle.so is not connected");
          return;
        }
        setCircleManagementOpen(true);
        setManageLoading(false);
        break;
      case "highlevel":
        if (!connectionStatus.highlevel) {
          setError("HighLevel is not connected");
          return;
        }
        setHighLevelModalOpen(true);
        setManageLoading(false);
        break;
      case "zoom":
        if (!connectionStatus.zoom) {
          setError("Zoom is not connected");
          return;
        }
        setZoomManagementOpen(true);
        setManageLoading(false);
        break;
      default:
        setManageLoading(false);
    }
  };

  const handleCircleModalClose = () => {
    setCircleModalOpen(false);
  };

  const handleCircleModalSuccess = async () => {
    await fetchIntegrationStatuses();
  };

  const handleCircleManagementBack = () => {
    setCircleManagementOpen(false);
  };

  const handleZoomManagementBack = () => {
    setZoomManagementOpen(false);
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}
      <div className="integrations-main">
        <div className="integrations-grid">
          {connectableIntegrations.map((integration) => (
            <div key={integration.id} className="integration-card">
              <div className="new-badge">NEW</div>
              <h2>{integration.title}</h2>
              <img
                src={integration.logo}
                alt={integration.title}
                className="integration-logo"
              />
              <p>{integration.description}</p>
              <div className="button-group-vertical">
                {connectionStatus[integration.id] ? (
                  <>
                    <Box
                      sx={{ m: 1, position: 'relative' }}>
                      <Button
                        className="disconnect-button"
                        sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                        onClick={() => handleDisconnect(integration.id)}
                        variant="outlined"
                        size="large"
                        color="error"
                        disabled={disconnectLoading && loadingId === integration.id}
                      >
                        {integration.disconnectText}
                      </Button>
                      {disconnectLoading && loadingId === integration.id && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: colors.red[500],
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                          }}
                        />
                      )}
                    </Box>
                    {connectionStatus[integration.id] === "expired" ? (
                      <Box
                        sx={{ m: 1, position: 'relative' }}>
                        <Button
                          className="refresh-button"
                          sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                          onClick={() => handleRefresh(integration.id)}
                          variant="outlined"
                          color="success"
                          size="large"
                          disabled={manageLoading && loadingId === integration.id}
                        >
                          {integration.refreshText}
                        </Button>
                        {manageLoading && loadingId === integration.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: colors.green[500],
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: '-12px',
                              marginLeft: '-12px',
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{ m: 1, position: 'relative' }}>
                        <Button
                          className="manage-button"
                          sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                          onClick={() => handleManage(integration.id)}
                          variant="outlined"
                          color="success"
                          size="large"
                          disabled={manageLoading && loadingId === integration.id}
                        >
                          {integration.manageText}
                        </Button>
                        {manageLoading && loadingId === integration.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: colors.green[500],
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: '-12px',
                              marginLeft: '-12px',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{ m: 1, position: 'relative' }}>
                    <Button
                      className="connect-button"
                      sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                      onClick={() => handleConnect(integration.id)}
                      variant="outlined"
                      color="primary"
                      size="large"
                      disabled={statusLoading}
                    >
                      {integration.connectText}
                    </Button>
                    {statusLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: colors.blue[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bannerSoon">This feature will be available soon.</div>

        <div className="integrations-grid">{upcomingIntegrations.map((integration) => (
          <div key={integration.id} className="integration-card">
            <h2>{integration.title}</h2>
            <img
              src={integration.logo}
              alt={integration.title}
              className="integration-logo"
            />
            <p>{integration.description}</p>
            <div className="button-group-vertical">
              {connectionStatus[integration.id] ? (
                <>
                  <Box
                    sx={{ m: 1, position: 'relative' }}>
                    <Button
                      className="disconnect-button"
                      sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                      onClick={() => handleDisconnect(integration.id)}
                      variant="outlined"
                      size="large"
                      color="error"
                      disabled={disconnectLoading && loadingId === integration.id}
                    >
                      {integration.disconnectText}
                    </Button>
                    {disconnectLoading && loadingId === integration.id && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: colors.red[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{ m: 1, position: 'relative' }}>
                    <Button
                      className="manage-button"
                      sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                      onClick={() => handleManage(integration.id)}
                      variant="outlined"
                      color="success"
                      size="large"
                      disabled={manageLoading && loadingId === integration.id}
                    >
                      {integration.manageText}
                    </Button>
                    {manageLoading && loadingId === integration.id && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: colors.green[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{ m: 1, position: 'relative' }}>
                  <Button
                    className="connect-button"
                    sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                    onClick={() => handleConnect(integration.id)}
                    variant="outlined"
                    color="primary"
                    size="large"
                    disabled={statusLoading}
                  >
                    {integration.connectText}
                  </Button>
                  {statusLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: colors.blue[500],
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              )}
            </div>
          </div>
        ))}
        </div>
      </div >
      {slackModalOpen && <SlackModal onClose={() => setSlackModalOpen(false)} />}
      <CircleModal
        isOpen={circleModalOpen}
        onClose={handleCircleModalClose}
        onSuccess={handleCircleModalSuccess}
        isAlreadyConnected={connectionStatus["circle-so"]}
      />
      {circleManagementOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '90vw', width: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <CircleManagement onBack={handleCircleManagementBack} />
          </div>
        </div>
      )}
      {zoomManagementOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '90vw', width: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <ZoomManagement onBack={handleZoomManagementBack} />
          </div>
        </div>
      )}
      {highLevelModalOpen && <HighLevelModal onClose={(event) => {
        setHighLevelModalOpen(false);
        if (event === "manualSync") setHighLevelContactSyncModalOpen(true);
      }} />}
      {highLevelContactSyncModealOpen && <HighLevelContactSync onClose={(event) => {
        setHighLevelContactSyncModalOpen(false);
        if (event === 'refreshStats' && highLevelModalOpen) {
          setTimeout(() => {
            const refreshEvent = new CustomEvent('refreshHighLevelStats');
            window.dispatchEvent(refreshEvent);
          }, 500);
        }
      }} />}
      
      {/* Zoom Disconnect Confirmation Dialog */}
      <Dialog
        open={confirmDisconnectOpen}
        onClose={handleCancelDisconnect}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '0.5rem',
          }
        }}
      >
        <DialogTitle 
          id="disconnect-dialog-title"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1a202c',
            paddingBottom: '0.5rem'
          }}
        >
          Disconnect Zoom Integration
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '1rem' }}>
          <DialogContentText 
            id="disconnect-dialog-description"
            sx={{
              color: '#4a5568',
              fontSize: '1rem',
              lineHeight: 1.7,
              marginBottom: '1rem'
            }}
          >
            Are you sure you want to disconnect the Zoom integration?
          </DialogContentText>
          
          <Box
            sx={{
              backgroundColor: '#fff5f5',
              border: '1px solid #feb2b2',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}
          >
            <Box sx={{ fontWeight: 600, color: '#c53030', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              This action will:
            </Box>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '1.5rem', color: '#4a5568' }}>
              <li style={{ marginBottom: '0.5rem' }}>Stop syncing new recordings from Zoom</li>
              <li style={{ marginBottom: '0.5rem' }}>Revoke ClientBloom's access to your Zoom account</li>
              <li>Remove the OAuth connection tokens</li>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: '#ebf8ff',
              border: '1px solid #90cdf4',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start'
            }}
          >
            <Box sx={{ fontSize: '1.25rem', flexShrink: 0 }}>ℹ️</Box>
            <Box sx={{ color: '#2c5282', fontSize: '0.9rem', lineHeight: 1.6 }}>
              <strong>Note:</strong> Previously synced calls and transcripts will remain in ClientBloom.
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem', gap: '0.75rem' }}>
          <Button 
            onClick={handleCancelDisconnect}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '0.625rem 1.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDisconnect} 
            variant="contained"
            color="error"
            autoFocus
            sx={{
              borderRadius: '8px',
              padding: '0.625rem 1.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)',
              boxShadow: '0 4px 12px rgba(197, 48, 48, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53e3e 0%, #9b2c2c 100%)',
                boxShadow: '0 6px 16px rgba(197, 48, 48, 0.4)',
              }
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IntegrationsMain;
